
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FlashcardDeck } from "@/types/flashcard";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import FlashcardReview from './FlashcardReview';
import { Loader2, Clock, PlusCircle, Library, BookOpen, AlertTriangle, Bookmark, BookOpenCheck } from "lucide-react";
import { useLocation, useNavigate } from 'react-router-dom';
import { transformDatabaseFlashcardDeck } from "@/lib/flashcard-utils";
import { useSpacedRepetitionScheduler } from "@/hooks/useSpacedRepetitionScheduler";
import CreateDeckDialog from './CreateDeckDialog';
import { AspectRatio } from "@/components/ui/aspect-ratio";

const FlashcardsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("due");
  const [decks, setDecks] = useState<FlashcardDeck[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [quizMistakesDecks, setQuizMistakesDecks] = useState<FlashcardDeck[]>([]);
  const [showMistakesOnly, setShowMistakesOnly] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { getDueFlashcards, getMistakeFlashcards } = useSpacedRepetitionScheduler();

  useEffect(() => {
    console.log('FlashcardsPage Component Mounted');
    console.log('Initial URL Search Params:', location.search);
    loadDecks();
  }, [location.search]);

  const loadDecks = async () => {
    setIsLoading(true);
    try {
      console.log('Loading decks start');
      
      const { data, error } = await supabase
        .from('flashcard_decks')
        .select('*')
        .order('updated_at', { ascending: false });
        
      if (error) throw error;

      console.log('Decks loaded successfully:', data);
      
      const transformedDecks = data?.map(deck => transformDatabaseFlashcardDeck(deck)) || [];
      
      const mistakesDecks = transformedDecks?.filter(deck => deck.name.includes('Quiz Mistakes')) || [];
      if (mistakesDecks.length > 0) {
        console.log(`Found ${mistakesDecks.length} quiz mistakes decks:`, mistakesDecks);
        setQuizMistakesDecks(mistakesDecks);
      } else {
        console.log('No quiz mistakes decks found.');
      }
      
      setDecks(transformedDecks);

      const params = new URLSearchParams(location.search);
      const fromQuiz = params.get('fromQuiz');
      const deckId = params.get('deckId');
      const showMistakes = params.get('showMistakes');
      
      if (deckId) {
        console.log(`Setting selected deck ID to ${deckId} from URL params`);
        setSelectedDeckId(deckId);
        setShowMistakesOnly(showMistakes === 'true');
      }
      
      if (fromQuiz === 'true' && deckId) {
        const dueCards = await getDueFlashcards(deckId);
        console.log(`Found ${dueCards.length} due cards for review in deck ${deckId}`);
        
        if (dueCards.length === 0) {
          toast.info("No flashcards due for review yet. They'll be scheduled for later review.");
        }
      }
    } catch (error) {
      console.error('Error in loadDecks:', error);
      toast.error('Failed to load decks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeckSelect = (deckId: string) => {
    console.log('Selected deck:', deckId);
    setSelectedDeckId(deckId);
    setActiveTab("due");
    
    const isMistakesDeck = decks.find(deck => 
      deck.id === deckId && deck.name.includes('Quiz Mistakes')
    );
    
    setShowMistakesOnly(!!isMistakesDeck);
    
    navigate(`/flashcards?deckId=${deckId}${isMistakesDeck ? '&showMistakes=true' : ''}`, { replace: true });
  };

  const handleShowAllMistakes = () => {
    setSelectedDeckId(undefined);
    setShowMistakesOnly(true);
    setActiveTab("due");
    navigate('/flashcards?showMistakes=true', { replace: true });
  };

  const handleBackToDecks = () => {
    setSelectedDeckId(undefined);
    setShowMistakesOnly(false);
    setActiveTab("decks");
    navigate('/flashcards', { replace: true });
  };

  const handleDeckCreated = () => {
    loadDecks();
    setActiveTab("decks");
    toast.success("New flashcard deck created successfully!");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 custom-scrollbar">
      <header className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2 bg-gradient-to-r from-[#8B5CF6] via-[#D946EF] to-[#0EA5E9] bg-clip-text text-transparent">
          Flashcards
        </h1>
        <p className="text-muted-foreground">
          Review and manage your flashcards for spaced repetition learning
        </p>
      </header>
      
      {selectedDeckId && (
        <div className="mb-4">
          <Button 
            variant="outline" 
            onClick={handleBackToDecks}
            className="mb-4 flex items-center gap-2"
          >
            ← Back to all decks
          </Button>
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
          <TabsList className="grid grid-cols-3 gap-2">
            <TabsTrigger value="due" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="hidden sm:inline">Due for Review</span>
              <span className="sm:hidden">Review</span>
            </TabsTrigger>
            <TabsTrigger value="decks" className="flex items-center gap-2">
              <Library className="w-4 h-4" />
              <span>Decks</span>
            </TabsTrigger>
            <TabsTrigger value="create" className="flex items-center gap-2">
              <PlusCircle className="w-4 h-4" />
              <span>Create</span>
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center w-full sm:w-auto justify-end gap-2">
            <CreateDeckDialog onDeckCreated={handleDeckCreated} />
          </div>
        </div>
        
        <TabsContent value="due" className="space-y-6">
          {quizMistakesDecks.length > 0 && !selectedDeckId && (
            <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700 mb-4 shadow-sm overflow-hidden">
              <CardContent className="pt-4 pb-2">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-amber-100 dark:bg-amber-900/50 p-2 mt-1">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-lg">Quiz Mistake Flashcards</h3>
                      <p className="text-sm text-muted-foreground">Review flashcards created from your quiz mistakes</p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleShowAllMistakes}
                    variant="outline" 
                    className="bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300 dark:bg-amber-900/50 dark:text-amber-300 dark:hover:bg-amber-800/70 dark:border-amber-700 flex items-center gap-2"
                  >
                    <BookOpenCheck className="h-4 w-4" />
                    Study Mistakes
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="overflow-hidden border border-border/50 shadow-md">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-b">
              <CardTitle className="text-xl font-bold text-primary">
                {showMistakesOnly 
                  ? "Quiz Mistakes Revision" 
                  : selectedDeckId 
                    ? "Deck Revision" 
                    : "Smart Revision Mode"}
              </CardTitle>
            </CardHeader>
            <CardContent className="custom-scrollbar p-6">
              <FlashcardReview 
                key={`${selectedDeckId || 'all'}-${showMistakesOnly}`}
                deckId={selectedDeckId} 
                showMistakesOnly={showMistakesOnly}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="decks" className="space-y-6 custom-scrollbar max-h-[calc(100vh-220px)]">
          {decks.length === 0 ? (
            <div className="text-center p-8 bg-muted/20 rounded-lg border border-dashed border-muted">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <BookOpen className="h-8 w-8 text-primary/60" />
              </div>
              <h2 className="text-xl font-bold mb-2">No Flashcard Decks</h2>
              <p className="text-muted-foreground mb-6">Create your first deck or complete a quiz to generate flashcards.</p>
              <CreateDeckDialog onDeckCreated={handleDeckCreated} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {decks.map(deck => (
                <Card 
                  key={deck.id} 
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md hover:translate-y-[-2px] overflow-hidden ${
                    deck.name.includes('Quiz Mistakes') ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-700/50' : ''
                  }`}
                  onClick={() => handleDeckSelect(deck.id)}
                >
                  <div className="relative h-2">
                    <div className={`absolute top-0 left-0 w-full h-full ${
                      deck.name.includes('Quiz Mistakes') 
                        ? 'bg-gradient-to-r from-amber-400 to-amber-300 dark:from-amber-600 dark:to-amber-500'
                        : 'bg-gradient-to-r from-purple-500 to-blue-500 dark:from-purple-600 dark:to-blue-600'
                    }`} />
                  </div>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {deck.name.includes('Quiz Mistakes') 
                          ? <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                          : <Bookmark className="h-4 w-4 text-primary" />
                        }
                        {deck.name}
                      </CardTitle>
                      <div className="px-2 py-1 bg-muted/30 rounded-full text-xs font-medium">
                        {deck.card_count || 0} cards
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{deck.description}</p>
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <div className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 rounded-full">
                        {deck.category}
                      </div>
                      <div className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 rounded-full">
                        {deck.subject}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="create">
          <Card className="border shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-b">
              <CardTitle className="text-xl font-bold text-primary">Create New Flashcard Deck</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="mt-2 mb-6">
                <CreateDeckDialog onDeckCreated={handleDeckCreated} showFullForm={true} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FlashcardsPage;
